import React, { useState, useEffect } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart, ArcElement, Tooltip, ChartOptions } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { motion } from "framer-motion";

Chart.register(ArcElement, Tooltip, ChartDataLabels);

const PieChartSteps = () => {
  // State to track if it's mobile viewport
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 3000);
    checkMobile();

    // Optional: update on resize if you want it to be dynamic
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const chartData = [
    { label: "Staking Rewards Pool", value: 15, color: "#FF2E63" },
    { label: "Community", value: 10, color: "#FF61A6" },
    { label: "Treasury & Reserve", value: 20, color: "#3ED598" },
    { label: "Advisors", value: 5, color: "#FFA41B" },
    { label: "Team & Founders", value: 20, color: "#3E92CC" },
    { label: "Private Sale", value: 5, color: "#A75D5D" },
    { label: "Public Sale", value: 25, color: "#A75D5D" },
  ];

  const data = {
    labels: chartData.map((item) => item.label),
    datasets: [
      {
        data: chartData.map((item) => item.value),
        backgroundColor: chartData.map((item) => item.color),
        borderColor: "#FFFFFF",
        borderWidth: 6,
        hoverOffset: 24,
      },
    ],
  };

  const options: ChartOptions<"doughnut"> = {
    cutout: "20%",
    layout: {
      padding: 20,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem: any) {
            return tooltipItem.label;
          },
        },
      },
      datalabels: {
        color: "#000",
        formatter: (value: number, context: any) => {
          const label = context.chart.data.labels?.[context.dataIndex] ?? "";
          return `${label}\n${value}%`;
        },
        font: {
          weight: "bold",
          size: 12,
        },
        display: (context: any) => {
          if (isMobile) return false; // Hide labels on mobile
          return context.dataset.data[context.dataIndex] > 5;
        },
      },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="w-full bg-white flex flex-col lg:flex-row items-center justify-center lg:justify-around gap-8 rounded-[26px] p-4 sm:p-6 md:p-10 shadow-lg"
    >
      {/* Left Side Content */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
        className="w-full flex flex-col justify-center items-start gap-8"
      >
        <div className="w-full flex flex-col items-start justify-center gap-3">
          <div className=" border-[#172B6B] pl-3 text-[#172B6B] font-bold text-lg sm:text-xl md:text-3xl ml-4 font-bungeeinline uppercase">
            Initial distribution plan
          </div>
        </div>

        {/* Custom Legend */}
        <div className="w-full space-y-3">
          {chartData.map((item, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-3"
            >
              <span
                className="w-1 h-6 rounded-full"
                style={{ backgroundColor: item.color }}
              ></span>
              <span className="text-gray-700 text-base sm:text-lg">
                {item.label}
              </span>
              <span
                className="ml-auto text-sm sm:text-base md:text-lg font-semibold"
                style={{ color: item.color }}
              >
                {item.value}%
              </span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Doughnut Chart */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
        className="w-full max-w-xs sm:max-w-md md:max-w-lg p-4"
      >
        <Doughnut data={data} options={options} />
      </motion.div>
    </motion.div>
  );
};

export default PieChartSteps;
